// Monitor de procesos en background. Sistemas → Procesos → Monitor de procesos.
// Lista los jobs del sri-worker (financiero.sri_jobs) y permite cancelar,
// reiniciar (reencolar) y eliminar. Auto-refresca cada 4 s.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, FileText, Loader2, Pause, Pencil, Play, Plus, RefreshCw, RotateCcw, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useSort, SortTh } from '@/features/control-bancario/shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Modal, fieldCls } from './shared';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };

interface Job {
  id: string; origen: string | null; tipo_comprobante: string | null;
  anio: number | null; mes: number | null; fecha_desde: string | null; fecha_hasta: string | null;
  estado: string; total_documentos: number | null; descargados: number; omitidos: number; errores: number;
  mensaje_error: string | null; iniciado_at: string | null; finalizado_at: string | null; created_at: string; usuario: string | null;
}

const ESTADOS = ['todos', 'pendiente', 'en_proceso', 'completado', 'error', 'cancelado'];
const BADGE: Record<string, string> = {
  pendiente: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  en_proceso: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  completado: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  cancelado: 'bg-muted text-muted-foreground',
};
const dt = (s: string | null) => (s ? new Date(s).toLocaleString(LOCALE) : '—');
const edad = (s: number | null | undefined) => s == null ? '' : s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s / 60)} min` : `${Math.floor(s / 3600)} h`;
const periodo = (j: Job) => (j.fecha_desde ? `${j.fecha_desde} → ${j.fecha_hasta ?? ''}` : j.anio ? `${String(j.mes ?? '').padStart(2, '0')}/${j.anio}` : '—');
const usd = (v: number | string | null) => (v == null ? '—' : Number(v).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' }));

// Cola de emisión electrónica (financiero.tickets)
interface Emision {
  id: string; origen: string | null; tipo_comprobante: string | null; serie_comprobante: string | null; clave_acceso: string | null;
  ruc_receptor: string | null; razon_social_receptor: string | null; importe_total: number | null; fecha_emision: string | null;
  estado_emision: string; mensaje_sri: string | null; numero_autorizacion: string | null; ride_url: string | null; updated_at: string;
}
const EMI_BADGE: Record<string, string> = {
  por_emitir: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  encolado: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  procesando: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  en_procesamiento: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  detenido: 'bg-muted text-muted-foreground',
  autorizado: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  rechazado: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  // El worker guarda 'devuelto'; se acepta también 'devuelta' por compatibilidad.
  devuelto: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  devuelta: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};
const EMI_REINTENTABLE = ['por_emitir', 'rechazado', 'devuelto', 'devuelta', 'error'];
const EMI_ROJO = ['rechazado', 'devuelto', 'devuelta', 'error'];
// Estados posibles de emisión (para el filtro por columna; fijo para alcanzar históricos)
const EMI_ESTADOS = ['por_emitir', 'encolado', 'procesando', 'en_procesamiento', 'detenido', 'autorizado', 'rechazado', 'devuelto', 'error'];
const urlDoc = (r: string | null) => (r ? r.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null);
// Caso especial: devuelto por el SRI con "CLAVE ACCESO REGISTRADA" → la clave ya existe en el
// SRI; reprocesar debe SALTAR el reenvío e ir directo a consultar la autorización.
const claveRegistrada = (e: Emision) => (e.estado_emision === 'devuelto' || e.estado_emision === 'devuelta') && /clave\s*(de\s*)?acceso\s*registrada/i.test(e.mensaje_sri || '');

// Patrones de error DINÁMICOS para el reintento automático (financiero.sri_reintento_patron)
interface Patron { id: string; patron: string; descripcion: string | null; activo: boolean; updated_at: string }
// Patrones fijos integrados en el worker (sri-worker/src/db.js · esErrorTransitorio): siempre activos.
const FIJOS = [
  'ECONNRESET / timeout / socket', 'fetch failed / network', 'TLS / handshake', 'certificado (hostname/IP, altname)',
  'javax.persistence', 'org.hibernate / GenericJDBC', 'soap:Server (javax/hibernate)',
];

export function Procesos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [estado, setEstado] = useState('todos');
  const [origen, setOrigen] = useState<'todos' | 'recibidos' | 'emitidos' | 'emisiones' | 'reintentos'>('todos');
  const [detalle, setDetalle] = useState<Job | null>(null);
  const { data: jobs = [], isFetching } = useQuery<Job[]>({
    queryKey: ['adm-procesos', estado],
    queryFn: async () => (await apiClient.post('/rpc/sri_jobs_listar', { _estado: estado, _limite: 100 }, FIN)).data || [],
    staleTime: 0, refetchInterval: 4000,  // monitor en vivo
  });

  // Estado del worker (heartbeat)
  const { data: worker } = useQuery<{ en_linea: boolean; segundos: number | null; host?: string; pid?: number }>({
    queryKey: ['adm-worker-estado'],
    queryFn: async () => (await apiClient.post('/rpc/sri_worker_estado', {}, FIN)).data,
    staleTime: 0, refetchInterval: 10000,
  });

  const accion = useMutation({
    mutationFn: (v: { fn: string; id: string; ok: string }) => apiClient.post(`/rpc/${v.fn}`, { _job_id: v.id }, FIN),
    onSuccess: (_d, v) => { toast.success(v.ok); void qc.invalidateQueries({ queryKey: ['adm-procesos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Cola de emisiones (financiero.tickets). El filtro de Estado se aplica del lado del
  // servidor (para alcanzar históricos, p. ej. devueltos viejos fuera de los más recientes);
  // los demás filtros de columna se aplican en cliente.
  const [emiFiltros, setEmiFiltros] = useState({ documento: '', receptor: '', total: '', fecha: '', estado: '', autorizacion: '' });
  const { data: emisiones = [] } = useQuery<Emision[]>({
    queryKey: ['adm-emisiones', emiFiltros.estado],
    queryFn: async () => (await apiClient.post('/rpc/emisiones_listar', { _estado: emiFiltros.estado || 'todos', _limite: 300 }, FIN)).data || [],
    staleTime: 0, refetchInterval: 5000,
  });
  const reintentarEmision = useMutation({
    mutationFn: (id: string) => apiClient.post('/rpc/emision_reintentar', { _ticket_id: id }, FIN),
    onSuccess: () => { toast.success('Emisión reencolada'); void qc.invalidateQueries({ queryKey: ['adm-emisiones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const reprocesarClave = useMutation({
    mutationFn: (id: string) => apiClient.post('/rpc/emision_reintentar', { _ticket_id: id }, FIN),
    onSuccess: () => { toast.success('Reprocesando: la clave ya está registrada en el SRI, consultando autorización'); void qc.invalidateQueries({ queryKey: ['adm-emisiones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const detenerEmision = useMutation({
    mutationFn: (id: string) => apiClient.post('/rpc/emision_detener', { _ticket_id: id }, FIN),
    onSuccess: () => { toast.success('Reconsulta detenida'); void qc.invalidateQueries({ queryKey: ['adm-emisiones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const reanudarEmision = useMutation({
    mutationFn: (id: string) => apiClient.post('/rpc/emision_reanudar', { _ticket_id: id }, FIN),
    onSuccess: () => { toast.success('Reconsulta reanudada'); void qc.invalidateQueries({ queryKey: ['adm-emisiones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Emisión electrónica: filtros por columna (contiene) + orden asc/desc por columna (excepto RIDE)
  const setEF = (k: keyof typeof emiFiltros, v: string) => setEmiFiltros((f) => ({ ...f, [k]: v }));
  const emiTexto = {
    documento: (e: Emision) => `${e.tipo_comprobante ?? ''} ${e.serie_comprobante ?? ''} ${e.clave_acceso ?? ''} ${e.origen ?? ''}`,
    receptor: (e: Emision) => `${e.razon_social_receptor ?? ''} ${e.ruc_receptor ?? ''}`,
    total: (e: Emision) => (e.importe_total == null ? '' : String(e.importe_total)),
    fecha: (e: Emision) => e.fecha_emision ?? '',
    autorizacion: (e: Emision) => e.numero_autorizacion ?? '',
  };
  const emisionesFiltradas = useMemo(() => {
    const inc = (val: string, q: string) => !q.trim() || val.toLowerCase().includes(q.trim().toLowerCase());
    return emisiones.filter((e) =>   // el estado ya viene filtrado del servidor
      inc(emiTexto.documento(e), emiFiltros.documento) &&
      inc(emiTexto.receptor(e), emiFiltros.receptor) &&
      inc(emiTexto.total(e), emiFiltros.total) &&
      inc(emiTexto.fecha(e), emiFiltros.fecha) &&
      inc(emiTexto.autorizacion(e), emiFiltros.autorizacion));
  }, [emisiones, emiFiltros]);  // eslint-disable-line react-hooks/exhaustive-deps
  const emiSort = useSort<Emision>(emisionesFiltradas, (e, key) => {
    switch (key) {
      case 'documento': return e.tipo_comprobante ?? '';
      case 'receptor': return e.razon_social_receptor ?? '';
      case 'total': return e.importe_total ?? null;
      case 'fecha': return e.fecha_emision ?? '';
      case 'estado': return e.estado_emision ?? '';
      case 'autorizacion': return e.numero_autorizacion ?? '';
      default: return '';
    }
  });

  const activo = (e: string) => e === 'pendiente' || e === 'en_proceso';
  const terminal = (e: string) => e === 'completado' || e === 'error' || e === 'cancelado';

  // Patrones de error dinámicos para el reintento automático (config global)
  const { data: patrones = [] } = useQuery<Patron[]>({
    queryKey: ['adm-reintento-patrones'],
    queryFn: async () => (await apiClient.post('/rpc/sri_reintento_patron_listar', {}, FIN)).data || [],
    staleTime: 0,
  });
  const [editPatron, setEditPatron] = useState<Partial<Patron> | null>(null);
  const guardarPatron = useMutation({
    mutationFn: (p: Partial<Patron>) => apiClient.post('/rpc/sri_reintento_patron_guardar', { _data: p }, FIN),
    onSuccess: () => { toast.success('Patrón guardado'); setEditPatron(null); void qc.invalidateQueries({ queryKey: ['adm-reintento-patrones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminarPatron = useMutation({
    mutationFn: (id: string) => apiClient.post('/rpc/sri_reintento_patron_eliminar', { _id: id }, FIN),
    onSuccess: () => { toast.success('Patrón eliminado'); void qc.invalidateQueries({ queryKey: ['adm-reintento-patrones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Separación por origen (pestañas)
  const jobsVista = useMemo(() => (origen === 'todos' ? jobs : jobs.filter((j) => j.origen === origen)), [jobs, origen]);
  const TABS: { v: typeof origen; label: string }[] = [
    { v: 'todos', label: 'Todos' }, { v: 'recibidos', label: 'Descarga recibidos' }, { v: 'emitidos', label: 'Descarga emitidos' }, { v: 'emisiones', label: 'Emisión electrónica' }, { v: 'reintentos', label: 'Reintentos' },
  ];
  const cuenta = (o: typeof origen) => (o === 'emisiones' ? emisiones.length : o === 'reintentos' ? patrones.length : o === 'todos' ? jobs.length : jobs.filter((j) => j.origen === o).length);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold flex items-center gap-2"><Activity className="w-5 h-5" /> Monitor de procesos</h1>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${worker?.en_linea ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}
            title={worker?.host ? `Worker en ${worker.host} (pid ${worker.pid})` : 'Worker SRI'}>
            <span className={`h-2 w-2 rounded-full ${worker?.en_linea ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
            {worker?.en_linea
              ? `Worker en línea${worker.segundos != null ? ` · hace ${edad(worker.segundos)}` : ''}`
              : worker?.segundos != null ? `Worker sin señal · hace ${edad(worker.segundos)}` : 'Worker sin señal'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {origen !== 'emisiones' && origen !== 'reintentos' && (
            <select className={`${fieldCls} w-44`} value={estado} onChange={(e) => setEstado(e.target.value)}>
              {ESTADOS.map((s) => <option key={s} value={s}>{s === 'todos' ? 'Todos los estados' : s}</option>)}
            </select>
          )}
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Procesos en background del worker SRI (descarga/emisión de comprobantes). Se actualiza automáticamente. El cancelar aplica a procesos activos; reiniciar reencola los terminados; eliminar quita los terminados.</p>

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button key={t.v} type="button" onClick={() => setOrigen(t.v)}
            className={`px-3 py-1.5 text-sm border-b-2 ${origen === t.v ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.label} <span className="text-xs text-muted-foreground">({cuenta(t.v)})</span>
          </button>
        ))}
      </div>

      {origen !== 'emisiones' && origen !== 'reintentos' && (
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Procesos ({jobsVista.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Origen / Tipo</th><th className="py-2 pr-3">Período</th><th className="py-2 pr-3">Estado</th>
              <th className="py-2 pr-3 text-right">Progreso</th><th className="py-2 pr-3">Usuario</th><th className="py-2 pr-3">Creado</th><th className="py-2 pr-3">Finalizado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {jobsVista.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin procesos</td></tr>}
              {jobsVista.map((j) => (
                <tr key={j.id} className="border-b last:border-0 align-top cursor-pointer hover:bg-muted/40" onClick={() => setDetalle(j)} title="Ver detalle / bitácora">
                  <td className="py-2 pr-3"><div className="font-medium capitalize">{j.origen ?? '—'}</div><div className="text-xs text-muted-foreground">{j.tipo_comprobante ?? ''}</div></td>
                  <td className="py-2 pr-3 text-xs">{periodo(j)}</td>
                  <td className="py-2 pr-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${BADGE[j.estado] ?? 'bg-muted'}`}>
                      {j.estado === 'en_proceso' && <Loader2 className="w-3 h-3 animate-spin" />}{j.estado}
                    </span>
                    {j.mensaje_error && <div className="mt-1 max-w-[16rem] truncate text-xs text-red-500" title={j.mensaje_error}>{j.mensaje_error}</div>}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-xs">
                    {j.total_documentos != null ? <span>{j.descargados}/{j.total_documentos}</span> : '—'}
                    {(j.omitidos > 0 || j.errores > 0) && <div className="text-muted-foreground">om {j.omitidos} · err {j.errores}</div>}
                  </td>
                  <td className="py-2 pr-3 text-xs">{j.usuario ?? '—'}</td>
                  <td className="py-2 pr-3 text-xs whitespace-nowrap">{dt(j.created_at)}</td>
                  <td className="py-2 pr-3 text-xs whitespace-nowrap">{dt(j.finalizado_at)}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {activo(j.estado) && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20" title="Cancelar" onClick={(ev) => { ev.stopPropagation(); accion.mutate({ fn: 'sri_cancelar_job', id: j.id, ok: 'Proceso cancelado' }); }}><XCircle className="w-4 h-4" /></button>
                    )}
                    {(terminal(j.estado) || j.estado === 'en_proceso') && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-primary hover:bg-muted ml-1" title={j.estado === 'en_proceso' ? 'Reiniciar (proceso colgado)' : 'Reiniciar (reencolar)'} onClick={async (ev) => {
                        ev.stopPropagation();
                        if (j.estado === 'en_proceso' && !(await dlg.confirmar('¿Reiniciar el proceso?', { detalle: 'El proceso figura EN CURSO. Reinícialo solo si está colgado: se reencolará desde cero.', peligro: true }))) return;
                        accion.mutate({ fn: 'sri_reintentar_job', id: j.id, ok: 'Proceso reencolado' });
                      }}><RotateCcw className="w-4 h-4" /></button>
                    )}
                    {terminal(j.estado) && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" onClick={async (ev) => { ev.stopPropagation(); if (await dlg.confirmar('¿Eliminar este proceso del historial?', { peligro: true })) accion.mutate({ fn: 'sri_eliminar_job', id: j.id, ok: 'Proceso eliminado' }); }}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      )}

      {origen === 'emisiones' && (
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Emisión electrónica ({emiSort.sorted.length}{emiSort.sorted.length !== emisiones.length ? ` de ${emisiones.length}` : ''})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <SortTh col="documento" sort={emiSort}>Documento</SortTh>
                <SortTh col="receptor" sort={emiSort}>Receptor</SortTh>
                <SortTh col="total" sort={emiSort} align="right">Total</SortTh>
                <SortTh col="fecha" sort={emiSort}>Fecha</SortTh>
                <SortTh col="estado" sort={emiSort}>Estado</SortTh>
                <SortTh col="autorizacion" sort={emiSort}>Autorización</SortTh>
                <th className="py-2"></th>
              </tr>
              <tr className="border-b text-xs">
                <th className="py-1 pr-3 font-normal"><input className={`${fieldCls} h-7 py-0 text-xs`} placeholder="Filtrar…" value={emiFiltros.documento} onChange={(ev) => setEF('documento', ev.target.value)} /></th>
                <th className="py-1 pr-3 font-normal"><input className={`${fieldCls} h-7 py-0 text-xs`} placeholder="Filtrar…" value={emiFiltros.receptor} onChange={(ev) => setEF('receptor', ev.target.value)} /></th>
                <th className="py-1 pr-3 font-normal"><input className={`${fieldCls} h-7 py-0 text-xs text-right`} placeholder="Filtrar…" value={emiFiltros.total} onChange={(ev) => setEF('total', ev.target.value)} /></th>
                <th className="py-1 pr-3 font-normal"><input className={`${fieldCls} h-7 py-0 text-xs`} placeholder="Filtrar…" value={emiFiltros.fecha} onChange={(ev) => setEF('fecha', ev.target.value)} /></th>
                <th className="py-1 pr-3 font-normal">
                  <select className={`${fieldCls} h-7 py-0 text-xs`} value={emiFiltros.estado} onChange={(ev) => setEF('estado', ev.target.value)}>
                    <option value="">Todos</option>
                    {EMI_ESTADOS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </th>
                <th className="py-1 pr-3 font-normal"><input className={`${fieldCls} h-7 py-0 text-xs`} placeholder="Filtrar…" value={emiFiltros.autorizacion} onChange={(ev) => setEF('autorizacion', ev.target.value)} /></th>
                <th className="py-1"></th>
              </tr>
            </thead>
            <tbody>
              {emiSort.sorted.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">{emisiones.length === 0 ? 'Sin emisiones' : 'Sin resultados para el filtro'}</td></tr>}
              {emiSort.sorted.map((e) => (
                <tr key={e.id} className="border-b last:border-0 align-top hover:bg-muted/40">
                  <td className="py-2 pr-3"><div className="font-medium">{e.tipo_comprobante ?? '—'}</div><div className="text-xs text-muted-foreground">{e.serie_comprobante || (e.clave_acceso ? `…${e.clave_acceso.slice(-8)}` : e.origen)}</div></td>
                  <td className="py-2 pr-3"><div className="max-w-[16rem] truncate" title={e.razon_social_receptor ?? ''}>{e.razon_social_receptor ?? '—'}</div><div className="text-xs text-muted-foreground">{e.ruc_receptor ?? ''}</div></td>
                  <td className="py-2 pr-3 text-right tabular-nums">{usd(e.importe_total)}</td>
                  <td className="py-2 pr-3 text-xs">{e.fecha_emision ?? '—'}</td>
                  <td className="py-2 pr-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${EMI_BADGE[e.estado_emision] ?? 'bg-muted'}`}>
                      {(e.estado_emision === 'procesando' || e.estado_emision === 'encolado' || e.estado_emision === 'en_procesamiento') && <Loader2 className="w-3 h-3 animate-spin" />}{e.estado_emision === 'en_procesamiento' ? 'en procesamiento (SRI)' : e.estado_emision}
                    </span>
                    {e.mensaje_sri && <div className={`mt-1 max-w-[18rem] truncate text-xs ${EMI_ROJO.includes(e.estado_emision) ? 'text-red-500' : 'text-muted-foreground'}`} title={e.mensaje_sri}>{e.mensaje_sri}</div>}
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs">{e.numero_autorizacion ?? '—'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {urlDoc(e.ride_url) && <a href={urlDoc(e.ride_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Ver RIDE"><FileText className="w-4 h-4" /></a>}
                    {claveRegistrada(e) && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 ml-1"
                        title="Reprocesar: la clave de acceso ya está registrada en el SRI. Salta el reenvío y consulta la autorización."
                        onClick={() => reprocesarClave.mutate(e.id)}><RefreshCw className="w-4 h-4" /></button>
                    )}
                    {!claveRegistrada(e) && (EMI_REINTENTABLE.includes(e.estado_emision) || e.estado_emision === 'procesando') && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-primary hover:bg-muted ml-1"
                        title={e.estado_emision === 'procesando' ? 'Reiniciar (emisión colgada)' : 'Reintentar emisión'}
                        onClick={async () => {
                          if (e.estado_emision === 'procesando' && !(await dlg.confirmar('¿Reiniciar la emisión?', { detalle: 'La emisión figura EN PROCESO. Reinifíciala solo si el worker está detenido/colgado. Si el comprobante ya se autorizó en el SRI, reencolarlo podría reemitirlo.', peligro: true }))) return;
                          reintentarEmision.mutate(e.id);
                        }}><RotateCcw className="w-4 h-4" /></button>
                    )}
                    {e.estado_emision === 'en_procesamiento' && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-amber-600 hover:bg-muted ml-1"
                        title="Detener la reconsulta (dejar de preguntar al SRI). El comprobante sigue en la cola del SRI."
                        onClick={() => detenerEmision.mutate(e.id)}><Pause className="w-4 h-4" /></button>
                    )}
                    {e.estado_emision === 'detenido' && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-primary hover:bg-muted ml-1"
                        title="Reanudar la reconsulta de autorización"
                        onClick={() => reanudarEmision.mutate(e.id)}><Play className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      )}

      {origen === 'reintentos' && (
      <div className="space-y-4">
        <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
          <p className="mb-1 text-foreground font-medium">¿Cómo funciona el reintento automático?</p>
          <p>Cuando la emisión electrónica falla, el worker decide si <b>reintenta</b> (fallo pasajero de red o del SRI) o la deja en <b>error</b> (rechazo real). Reintenta cuando el mensaje del SRI <b>contiene</b> alguno de los patrones de abajo, con espera creciente (30s, 90s, 270s… hasta 10 min). Los patrones son <b>globales</b> (aplican a todas las empresas) y el cotejo <b>no distingue mayúsculas</b>.</p>
          <p className="mt-2 text-foreground font-medium">Patrones fijos (siempre activos, integrados en el worker):</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {FIJOS.map((f) => <span key={f} className="rounded bg-background px-1.5 py-0.5 font-mono text-[11px] border">{f}</span>)}
          </div>
        </div>

        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Patrones adicionales ({patrones.length})</CardTitle>
            <button type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90"
              onClick={() => setEditPatron({ patron: '', descripcion: '', activo: true })}>
              <Plus className="w-4 h-4" /> Nuevo patrón
            </button>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Patrón (fragmento del mensaje)</th><th className="py-2 pr-3">Descripción</th><th className="py-2 pr-3">Estado</th><th className="py-2 pr-3">Actualizado</th><th className="py-2"></th>
              </tr></thead>
              <tbody>
                {patrones.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin patrones adicionales. Con los fijos basta para los fallos de red habituales; agrega uno solo si ves un error del SRI recurrente que debería reintentarse.</td></tr>}
                {patrones.map((p) => (
                  <tr key={p.id} className="border-b last:border-0 align-top hover:bg-muted/40">
                    <td className="py-2 pr-3 font-mono text-xs break-words max-w-[22rem]">{p.patron}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground max-w-[16rem]">{p.descripcion || '—'}</td>
                    <td className="py-2 pr-3">
                      <button type="button" onClick={() => guardarPatron.mutate({ id: p.id, patron: p.patron, descripcion: p.descripcion, activo: !p.activo })}
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${p.activo ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-muted text-muted-foreground'}`}
                        title="Clic para activar/desactivar">{p.activo ? 'Activo' : 'Inactivo'}</button>
                    </td>
                    <td className="py-2 pr-3 text-xs whitespace-nowrap">{dt(p.updated_at)}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-primary hover:bg-muted" title="Editar" onClick={() => setEditPatron(p)}><Pencil className="w-4 h-4" /></button>
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar('¿Eliminar este patrón?', { peligro: true })) eliminarPatron.mutate(p.id); }}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
      )}

      {editPatron && (
        <Modal title={editPatron.id ? 'Editar patrón' : 'Nuevo patrón de reintento'} onClose={() => setEditPatron(null)}>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Patrón — fragmento a buscar en el mensaje de error *</label>
              <input className={fieldCls} value={editPatron.patron ?? ''} autoFocus
                onChange={(e) => setEditPatron({ ...editPatron, patron: e.target.value })}
                placeholder="p. ej. servicio no disponible" />
              <p className="mt-1 text-[11px] text-muted-foreground">Cotejo por «contiene», sin distinguir mayúsculas. Usa un texto corto y característico del error.</p>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Descripción (opcional)</label>
              <input className={fieldCls} value={editPatron.descripcion ?? ''}
                onChange={(e) => setEditPatron({ ...editPatron, descripcion: e.target.value })}
                placeholder="Para qué caso lo agregaste" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editPatron.activo ?? true} onChange={(e) => setEditPatron({ ...editPatron, activo: e.target.checked })} />
              Activo
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted" onClick={() => setEditPatron(null)}>Cancelar</button>
              <button type="button" className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50"
                disabled={!editPatron.patron?.trim() || guardarPatron.isPending}
                onClick={() => guardarPatron.mutate(editPatron)}>Guardar</button>
            </div>
          </div>
        </Modal>
      )}

      {detalle && <JobDetalle job={detalle} onClose={() => setDetalle(null)} />}
    </div>
  );
}

// Detalle de un proceso + bitácora de eventos
interface Evt { id: number; nivel: string; mensaje: string; created_at: string }
const NIVEL: Record<string, string> = {
  info: 'text-sky-600 dark:text-sky-400', warn: 'text-amber-600 dark:text-amber-400',
  error: 'text-red-600 dark:text-red-400', ok: 'text-emerald-600 dark:text-emerald-400',
};
function JobDetalle({ job, onClose }: { job: Job; onClose: () => void }) {
  const enVivo = job.estado === 'pendiente' || job.estado === 'en_proceso';
  const { data: eventos = [], isFetching } = useQuery<Evt[]>({
    queryKey: ['adm-proc-eventos', job.id],
    queryFn: async () => (await apiClient.post('/rpc/sri_job_eventos_listar', { _job_id: job.id }, FIN)).data || [],
    staleTime: 0, refetchInterval: enVivo ? 3000 : false,
  });
  const Item = ({ k, v }: { k: string; v: React.ReactNode }) => (
    <div><div className="text-xs text-muted-foreground">{k}</div><div className="text-sm">{v}</div></div>
  );
  return (
    <Modal title={`Proceso · ${job.origen ?? ''} ${job.tipo_comprobante ?? ''}`} onClose={onClose} wide>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Item k="Estado" v={<span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${BADGE[job.estado] ?? 'bg-muted'}`}>{enVivo && <Loader2 className="w-3 h-3 animate-spin" />}{job.estado}</span>} />
        <Item k="Período" v={periodo(job)} />
        <Item k="Progreso" v={job.total_documentos != null ? `${job.descargados}/${job.total_documentos} · om ${job.omitidos} · err ${job.errores}` : '—'} />
        <Item k="Usuario" v={job.usuario ?? '—'} />
        <Item k="Creado" v={dt(job.created_at)} />
        <Item k="Iniciado" v={dt(job.iniciado_at)} />
        <Item k="Finalizado" v={dt(job.finalizado_at)} />
      </div>
      {job.mensaje_error && <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700 dark:border-red-900/40 dark:bg-red-900/15 dark:text-red-300">{job.mensaje_error}</div>}

      <div className="mt-4 flex items-center gap-2">
        <h4 className="text-sm font-semibold">Bitácora ({eventos.length})</h4>
        {isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        {enVivo && <span className="text-xs text-muted-foreground">· en vivo</span>}
      </div>
      <div className="mt-2 max-h-80 overflow-auto rounded-md border">
        {eventos.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Sin eventos registrados</p>}
        {eventos.map((e) => (
          <div key={e.id} className="flex gap-3 border-b px-3 py-1.5 text-sm last:border-0">
            <span className="shrink-0 font-mono text-xs text-muted-foreground">{new Date(e.created_at).toLocaleTimeString(LOCALE)}</span>
            <span className={`shrink-0 w-12 text-xs font-semibold uppercase ${NIVEL[e.nivel] ?? 'text-muted-foreground'}`}>{e.nivel}</span>
            <span className="break-words">{e.mensaje}</span>
          </div>
        ))}
      </div>
    </Modal>
  );
}
